import dayjs from 'dayjs'
import { Check } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { CompletedCourse, LightweightUser } from 'src/v3/manage/manage.slice'
import { Course } from 'src/v3/core/types'

type ThresholdModalProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  completedAssignmentsWithinThreshold: CompletedCourse[]
  users: LightweightUser[]
  courses: Course[]
  isSubmitting?: boolean
}

export default function ThresholdModal({
  open,
  onClose,
  onConfirm,
  completedAssignmentsWithinThreshold,
  users,
  courses,
  isSubmitting = false,
}: ThresholdModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: 'background.default',
          },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Credit Completed Users</DialogTitle>
      <DialogContent>
        <Typography className="mb-4">
          This is a list of completed courses by users in this assignment within
          the time threshold set by your linked service:
        </Typography>
        <Box
          className="grid grid-cols-3 my-2 overflow-y-auto max-h-60"
          sx={{
            border: 1,
            borderColor: 'primary.dark',
          }}
        >
          <Box
            className="p-2 py-1 text-white"
            sx={{
              bgcolor: 'primary.dark',
            }}
          >
            User
          </Box>
          <Box
            className="p-2 py-1 text-white"
            sx={{
              bgcolor: 'primary.dark',
            }}
          >
            Course
          </Box>
          <Box
            className="p-2 py-1 text-white"
            sx={{
              bgcolor: 'primary.dark',
            }}
          >
            Completed
          </Box>
          {completedAssignmentsWithinThreshold?.map((ca, i) => (
            <Box key={ca.userID + ca.courseID} className="contents">
              <Box
                className="px-2 py-1"
                sx={{
                  bgcolor: i % 2 ? 'background.paperDark' : 'background.paper',
                }}
              >
                {users.find((u) => u.id === ca.userID)?.firstName}{' '}
                {users.find((u) => u.id === ca.userID)?.lastName}
              </Box>
              <Box
                className="px-2 py-1"
                sx={{
                  bgcolor: i % 2 ? 'background.paperDark' : 'background.paper',
                }}
              >
                {courses.find((c) => c.id === ca.courseID)?.name}
              </Box>
              <Box
                className="px-2 py-1"
                sx={{
                  bgcolor: i % 2 ? 'background.paperDark' : 'background.paper',
                }}
              >
                {dayjs(ca.dateCompleted).format('MM/DD/YYYY')}
              </Box>
            </Box>
          ))}
        </Box>
        <Typography className="mt-4 mb-2">
          Would you like to mark these courses as complete for this assignment?
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-2">
          <strong>If you select YES:</strong> Users will not have to retake
          recently completed courses. All courses listed above will be marked as
          complete for their respective users for this assignment.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>If you select NO:</strong> Users will have to take every
          course in the assignment, even those that they have recently
          completed. You can still give credit for individual courses for users
          or change this selection later if desired.
        </Typography>
      </DialogContent>
      <DialogActions className="gap-2 p-6">
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
          size="large"
        >
          No
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          size="large"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Check />
            )
          }
        >
          {isSubmitting ? 'Processing...' : 'Yes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
